// Descriptive catalog of the role model for the admin screens: built-in role metadata,
// permission groups and labels, and which permissions the server also reads from the
// roles/{id} documents. The rules themselves live in rolePermissions.js; nothing here
// grants or denies anything.
import { ROLES, PERMISSIONS as P } from './rolePermissions';

// One entry per built-in role (ROLES), in ROLES order. A roles/{id} document's
// displayName/description/color override these; `scope` is what the server and
// firestore.rules let the role reach, which no document changes.
export const BUILT_IN_ROLE_META = {
    [ROLES.SUPER_ADMIN]: {
        displayName: 'Super Administrator',
        description: 'Full system access with no limitations',
        color: '#9c27b0',
        isSystemRole: true,
        scope: 'Every company. Always holds every permission.',
    },
    [ROLES.ORG_ADMIN]: {
        displayName: 'Organization Admin',
        description: "Company Admin across its organization's companies, plus carrier cost, payments and user management for them. Not a platform role.",
        color: '#0f766e',
        isSystemRole: true,
        scope: "Its own companies and its organization's member companies.",
    },
    [ROLES.ADMIN]: {
        displayName: 'Administrator',
        description: 'Administrative access to manage the system',
        color: '#2196f3',
        isSystemRole: true,
        scope: 'Every company (platform staff).',
    },
    [ROLES.USER]: {
        displayName: 'Company Administrator',
        description: 'Company-level access for daily operations',
        color: '#4caf50',
        isSystemRole: true,
        scope: 'Its connected companies. Sees charges as billed, never carrier cost.',
    },
    [ROLES.ACCOUNTING]: {
        displayName: 'Accounting',
        description: 'Access to billing, invoicing, and financial reports',
        color: '#6366f1',
        isSystemRole: true,
        scope: 'Billing back office across every company.',
    },
    [ROLES.COMPANY_STAFF]: {
        displayName: 'Company Staff',
        description: 'Basic operational access for company staff',
        color: '#00bcd4',
        isSystemRole: true,
        scope: 'Its connected companies. Sees charges as billed, never carrier cost.',
    },
    [ROLES.MANUFACTURER]: {
        displayName: 'Manufacturer',
        description: 'Limited access for manufacturing partners',
        color: '#607d8b',
        isSystemRole: false,
        scope: 'Its connected companies. Manages its own team (manufacturer roles only).',
    },
    [ROLES.MANUFACTURER_STAFF]: {
        displayName: 'Manufacturer Staff',
        description: "A manufacturer's staff: manufacturer access without user management",
        color: '#78909c',
        isSystemRole: true,
        scope: 'As Manufacturer, without user management.',
    },
    [ROLES.CUSTOMER]: {
        displayName: 'Customer Portal',
        description: 'End-user customer with limited admin access to view shipments and invoices',
        color: '#00bcd4',
        isSystemRole: true,
        scope: 'Its connected companies, narrowed to its listed customers.',
    },
    // Multi-Leg operational roles (spec §14). Mirrors functions/firestore.rules
    // `isOpsRole / isBrokerage / isWarehouse` predicates so an admin who
    // assigns one of these gets the corresponding leg-doc visibility.
    [ROLES.OPERATIONS]: {
        displayName: 'Operations',
        description: 'Multi-leg ops: schedule pickups, update leg statuses, view all routing legs',
        color: '#6366f1',
        isSystemRole: true,
        scope: 'Routing legs of every type. No app permissions are defined yet.',
    },
    [ROLES.DISPATCH]: {
        displayName: 'Dispatch',
        description: 'Routing and dispatch: assign carriers, book pickups, drive leg execution',
        color: '#0ea5e9',
        isSystemRole: true,
        scope: 'Routing legs of every type. No app permissions are defined yet.',
    },
    [ROLES.BROKERAGE]: {
        displayName: 'Brokerage',
        description: 'Customs broker: visibility on customs legs and customs documents',
        color: '#a855f7',
        isSystemRole: true,
        scope: 'Customs legs only. No app permissions are defined yet.',
    },
    [ROLES.WAREHOUSE]: {
        displayName: 'Warehouse',
        description: 'Warehouse staff: visibility on warehouse legs and inventory transfers',
        color: '#0d9488',
        isSystemRole: true,
        scope: 'Warehouse legs only. No app permissions are defined yet.',
    },
};

export const BUILT_IN_ROLE_IDS = Object.values(ROLES);
export const isBuiltInRole = (roleId) => BUILT_IN_ROLE_IDS.includes(roleId);

// Permissions the server also resolves from roles/{id}.permissions, the way the client's
// hasPermission() does: shipmentAccess.js PERMISSION_BASELINE and documentAccess.js
// roleHasCarrierConfirmation. Every other permission only shapes the app's screens and
// menus; the callables and firestore.rules check the role itself.
export const SERVER_READ_PERMISSIONS = [
    P.MANUAL_STATUS_OVERRIDE,
    P.ARCHIVE_SHIPMENT,
    P.EDIT_SHIPMENTS,
    P.CREATE_SHIPMENTS,
    P.GENERATE_CARRIER_CONFIRMATIONS,
    P.VIEW_CARRIER_CONFIRMATION,
];

// Every permission in exactly one group (roleCatalog.test.js checks it).
export const PERMISSION_GROUPS = [
    { id: 'access', label: 'Dashboard & access', permissions: [P.VIEW_DASHBOARD, P.VIEW_ADMIN_DASHBOARD] },
    {
        id: 'users',
        label: 'Users',
        permissions: [P.VIEW_USERS, P.CREATE_USERS, P.EDIT_USERS, P.DELETE_USERS, P.MANAGE_USER_ROLES, P.INVITE_USERS, P.RESET_USER_PASSWORD],
    },
    {
        id: 'companies',
        label: 'Companies',
        permissions: [P.VIEW_COMPANIES, P.CREATE_COMPANIES, P.EDIT_COMPANIES, P.DELETE_COMPANIES, P.VIEW_ALL_COMPANIES],
    },
    {
        id: 'organizations',
        label: 'Organizations & hierarchy',
        permissions: [
            P.VIEW_ORGANIZATIONS, P.CREATE_ORGANIZATIONS, P.EDIT_ORGANIZATIONS, P.DELETE_ORGANIZATIONS,
            P.VIEW_HIERARCHY, P.VIEW_HIERARCHY_TREE, P.VIEW_DESCENDANT_DATA,
            P.CREATE_DIVISIONS, P.EDIT_DIVISIONS, P.DELETE_DIVISIONS, P.MOVE_DIVISIONS, P.MANAGE_CHILD_COMPANIES,
            P.CONFIGURE_BILLING_MODE, P.CONFIGURE_INHERITANCE, P.MANAGE_HIERARCHY_ACCESS,
            P.VIEW_HIERARCHY_REPORTS, P.EXPORT_HIERARCHY_DATA,
        ],
    },
    {
        id: 'shipments',
        label: 'Shipments & quotes',
        permissions: [
            P.VIEW_SHIPMENTS, P.VIEW_ALL_SHIPMENTS, P.CREATE_SHIPMENTS, P.USE_QUICKSHIP, P.USE_LIVE_RATES,
            P.EDIT_SHIPMENTS, P.DELETE_SHIPMENTS, P.EXPORT_SHIPMENTS, P.MANAGE_DRAFT_SHIPMENTS, P.REVIEW_SHIPMENTS,
            P.ARCHIVE_SHIPMENT, P.CANCEL_SHIPMENT, P.RESOLVE_BOOKING_RECONCILE,
            P.VIEW_FOLLOW_UPS, P.VIEW_FOLLOWUPS, P.CREATE_QUOTES, P.VIEW_QUOTES,
        ],
    },
    {
        id: 'documents',
        label: 'Shipment documents',
        permissions: [P.VIEW_DOCUMENTS, P.VIEW_BOL, P.VIEW_CARRIER_CONFIRMATION, P.GENERATE_CARRIER_CONFIRMATIONS],
    },
    {
        id: 'forms',
        label: 'Shipment fields & forms',
        permissions: [
            P.VIEW_BILL_TYPE, P.VIEW_ETA_FIELDS, P.EDIT_ETAS, P.EDIT_SHIPMENT_REFERENCES, P.EDIT_CARRIER_TRACKING,
            P.SELECT_QUICKSHIP_CARRIER, P.SELECT_SHIP_FROM, P.SELECT_SHIP_TO,
            P.VIEW_SHIPFROM_ADDRESSES, P.VIEW_SHIPTO_ADDRESSES,
            P.USE_SWITCH_TO_LIVE_RATES, P.USE_SHIP_LATER, P.USE_BOOK_SHIPMENT,
            P.VIEW_DECLARED_VALUE, P.VIEW_FREIGHT_CLASS,
        ],
    },
    {
        id: 'cost',
        label: 'Cost & rate visibility',
        permissions: [P.VIEW_SHIPMENT_COSTS, P.VIEW_SHIPMENT_FINANCIALS, P.VIEW_RATE_PRICING, P.VIEW_RATE_BREAKDOWN, P.SHOW_RATE_FILTERS],
    },
    {
        id: 'customers',
        label: 'Customers & addresses',
        permissions: [
            P.VIEW_CUSTOMERS, P.CREATE_CUSTOMERS, P.EDIT_CUSTOMERS, P.DELETE_CUSTOMERS, P.VIEW_ALL_CUSTOMERS, P.SET_DEFAULT_ADDRESSES,
            P.MANAGE_ADDRESSES, P.VIEW_ADDRESSES, P.CREATE_ADDRESSES, P.EDIT_ADDRESSES, P.DELETE_ADDRESSES,
            P.VIEW_ALL_ADDRESSES, P.EXPORT_ADDRESSES,
        ],
    },
    {
        id: 'billing',
        label: 'Billing & payments',
        permissions: [
            P.VIEW_BILLING, P.VIEW_ALL_INVOICES, P.CREATE_INVOICES, P.EDIT_INVOICES, P.DELETE_INVOICES, P.GENERATE_INVOICES,
            P.MANAGE_PAYMENT_TERMS, P.VIEW_PAYMENTS, P.MANAGE_AP_PROCESSING, P.MANAGE_MARKUPS,
        ],
    },
    {
        id: 'commissions',
        label: 'Sales commissions',
        permissions: [
            P.VIEW_COMMISSIONS, P.MANAGE_SALES_PERSONS, P.MANAGE_SALES_TEAMS, P.CALCULATE_COMMISSIONS,
            P.GENERATE_COMMISSION_REPORTS, P.SCHEDULE_COMMISSION_REPORTS,
        ],
    },
    {
        id: 'carriers',
        label: 'Carriers & brokers',
        permissions: [
            P.VIEW_CARRIERS, P.CREATE_CARRIERS, P.EDIT_CARRIERS, P.DELETE_CARRIERS, P.MANAGE_CARRIERS,
            P.MANAGE_CARRIER_KEYS, P.MANAGE_EDI_MAPPING, P.MANAGE_BROKERS,
        ],
    },
    { id: 'tracking', label: 'Tracking', permissions: [P.VIEW_TRACKING, P.UPDATE_TRACKING, P.MANUAL_STATUS_OVERRIDE] },
    {
        id: 'reports',
        label: 'Reports & analytics',
        permissions: [P.VIEW_REPORTS, P.CREATE_REPORTS, P.SCHEDULE_REPORTS, P.VIEW_ALL_REPORTS, P.EXPORT_REPORTS, P.VIEW_ANALYTICS],
    },
    {
        id: 'profile',
        label: 'Profile & notifications',
        permissions: [P.VIEW_PROFILE, P.EDIT_PROFILE, P.VIEW_NOTIFICATIONS, P.MANAGE_NOTIFICATIONS],
    },
    {
        id: 'system',
        label: 'System, integrations & AI',
        permissions: [
            P.VIEW_SETTINGS, P.MANAGE_SETTINGS, P.MANAGE_SYSTEM_SETTINGS, P.MANAGE_ROLES, P.MANAGE_INTEGRATIONS,
            P.USE_AI_AGENT, P.USE_ADVANCED_ROUTING, P.VIEW_DEVELOPER_PORTAL_V2, P.VIEW_ATLAS_AGENT,
        ],
    },
];

const LABEL_WORDS = {
    ai: 'AI', ap: 'AP', bol: 'BOL', edi: 'EDI', eta: 'ETA', etas: 'ETAs', quickship: 'QuickShip',
    shipfrom: 'ship-from', shipto: 'ship-to', v2: '2.0', followups: 'follow-ups (legacy key)', follow: 'follow-',
};

// 'view_shipment_costs' -> 'View shipment costs'
export const permissionLabel = (permission) => {
    const words = String(permission).split('_').map((w) => LABEL_WORDS[w] || w);
    const text = words.join(' ').replace('follow- ups', 'follow-ups');
    return text.charAt(0).toUpperCase() + text.slice(1);
};
